/**
 * Custom hooks for Receivers API (via Company Associations)
 *
 * Provides hooks for listing receivers associated with a shipper company
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface ReceiverItem {
  company_id: string;
  company_name: string;
  legal_name: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  headquarters_address: string | null;
  company_type: string | null;
  country: string | null;
  activeContainers: number;
  totalContainers: number;
  lastContainerDate: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  total_value_usd: number | null;
}

export interface ReceiversListResponse {
  receivers: ReceiverItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface CreateAssociationRequest {
  shipper_company_id: string;
  receiver_company_id: string;
}

export interface CreateAssociationResponse {
  success: boolean;
  message: string;
  company_id_1: string;
  company_id_2: string;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const RECEIVERS_QUERY_KEYS = {
  all: ["receivers"] as const,
  byShipper: (shipperId: string) =>
    [...RECEIVERS_QUERY_KEYS.all, "shipper", shipperId] as const,
};

// ============================================================================
// LIST RECEIVERS HOOK
// ============================================================================

/**
 * Hook to fetch receivers associated with a shipper company
 *
 * @param shipperCompanyId - UUID of the shipper company
 * @param enabled - Whether the query should be enabled
 *
 * Returns receivers with:
 * - Company info (name, email, country)
 * - Container statistics (active, total, last date)
 * - Financial info (total value)
 */
export function useReceivers(shipperCompanyId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: RECEIVERS_QUERY_KEYS.byShipper(shipperCompanyId || ""),
    queryFn: async (): Promise<ReceiversListResponse> => {
      if (!shipperCompanyId) throw new Error("No shipper company ID provided");
      return await apiGet<ReceiversListResponse>("/company-associations/receivers", {
        params: { shipper_company_id: shipperCompanyId },
      });
    },
    enabled: enabled && !!shipperCompanyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// CREATE ASSOCIATION MUTATION
// ============================================================================

/**
 * Hook to create a company association between shipper and receiver
 *
 * Used when manually adding a new receiver from the container creation form.
 * This ensures the receiver appears in the shipper's receivers list.
 */
export function useCreateReceiverAssociation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAssociationRequest): Promise<CreateAssociationResponse> => {
      return await apiPost<CreateAssociationResponse>("/company-associations/create", data);
    },
    onSuccess: (_data, variables) => {
      // Invalidate receivers list to refetch with the new association
      queryClient.invalidateQueries({
        queryKey: RECEIVERS_QUERY_KEYS.byShipper(variables.shipper_company_id),
      });
    },
  });
}
